import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { timesheetService, PeriodHistoryItem, TimesheetEntryUI, NotSubmittedItem } from '../../services/timesheetService'
import { taskService } from '../../services/taskService'
import { dataService } from '../../services/dataService'
import { Clock, Check, X, ChevronDown, ChevronUp, RotateCcw, Search, Filter } from '../icons/Icons'
import s from './shared.module.css'

interface ResourceOption {
  id: string
  name: string
  email: string
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  approved: { bg: '#d1e7dd', text: '#0f5132', label: 'Approved' },
  submitted: { bg: '#fff4ce', text: '#664d03', label: 'Pending' },
  rejected: { bg: '#f8d7da', text: '#842029', label: 'Rejected' },
  open: { bg: '#e2e3e5', text: '#41464b', label: 'Open' },
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

interface DetailRow {
  project_id: string
  project_name: string
  task_id?: string
  task_name: string
  is_billable: boolean
  monday: number
  tuesday: number
  wednesday: number
  thursday: number
  friday: number
  saturday: number
  sunday: number
  monday_comment?: string
  tuesday_comment?: string
  wednesday_comment?: string
  thursday_comment?: string
  friday_comment?: string
  saturday_comment?: string
  sunday_comment?: string
}

export default function TimesheetHistory() {
  const { organization, user } = useAuth()
  const { isManager, isResource } = usePermissions()
  const orgId = organization!.id

  const [history, setHistory] = useState<PeriodHistoryItem[]>([])
  const [notSubmitted, setNotSubmitted] = useState<NotSubmittedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [filterStatus, setFilterStatus] = useState('all')
  const [filterResource, setFilterResource] = useState('all')
  const [filterStartDate, setFilterStartDate] = useState('')
  const [filterEndDate, setFilterEndDate] = useState('')
  const [resourceSearch, setResourceSearch] = useState('')

  const [resources, setResources] = useState<ResourceOption[]>([])
  const [projects, setProjects] = useState<{ id: string; title: string }[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [detailRows, setDetailRows] = useState<{ [periodId: string]: DetailRow[] }>({})
  const [detailLoading, setDetailLoading] = useState<string | null>(null)
  const [reopenLoading, setReopenLoading] = useState<string | null>(null)
  const [confirmReopenId, setConfirmReopenId] = useState<string | null>(null)

  const canReopen = isManager

  useEffect(() => {
    dataService.getProjects(orgId).then((p: any[]) => setProjects(p)).catch(() => {})
    const loadResources = async () => {
      try {
        const res = await dataService.getResources(orgId)
        const opts = res.map((r: any) => ({
          id: r.id,
          name: r.title || r.email || 'Unknown',
          email: r.email || '',
        }))
        setResources(opts)
      } catch { /* ignore */ }
    }
    loadResources()
  }, [orgId])

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const filters: any = {}
      if (filterStatus !== 'all') filters.status = filterStatus
      if (filterResource !== 'all') {
        const res = resources.find(r => r.id === filterResource)
        if (res) filters.userId = res.id
      }
      if (filterStartDate) filters.startDate = filterStartDate
      if (filterEndDate) filters.endDate = filterEndDate

      const data = await timesheetService.getTimesheetPeriodHistory(orgId, filters)
      setHistory(data)

      const nsFilters: any = {}
      if (filterStartDate) nsFilters.startDate = filterStartDate
      if (filterEndDate) nsFilters.endDate = filterEndDate
      const nsData = await timesheetService.getNotSubmittedTimesheets(orgId, nsFilters)
      setNotSubmitted(nsData)
    } catch (err: any) {
      setError(err?.message || 'Failed to load timesheet history')
    } finally {
      setLoading(false)
    }
  }, [orgId, filterStatus, filterResource, filterStartDate, filterEndDate, resources])

  useEffect(() => {
    if (resources.length > 0 || filterResource === 'all') {
      loadHistory()
    }
  }, [loadHistory, resources.length])

  // Resources view: filter to own periods only
  const visibleHistory = useMemo(() => {
    if (isResource && !isManager) {
      return history.filter(h => h.submitted_by === user?.id)
    }
    return history
  }, [history, isResource, isManager, user?.id])

// Group not-submitted by resource
  const groupedNotSubmitted = useMemo(() => {
    const groups: { [key: string]: { name: string; email: string; items: NotSubmittedItem[] } } = {}
    const visible = isResource && !isManager
      ? notSubmitted.filter(n => n.resource_email === user?.email)
      : notSubmitted
    visible.forEach(item => {
      const key = item.resource_id || 'unknown'
      const name = item.resource_name || item.resource_email || 'Unknown'
      const email = item.resource_email || ''
      if (!groups[key]) groups[key] = { name, email, items: [] }
      groups[key].items.push(item)
    })
    return groups
  }, [notSubmitted, isResource, isManager, user?.email])

  // Apply resource name search filter to not-submitted
  const filteredNotSubmitted = useMemo(() => {
    if (!resourceSearch.trim()) return groupedNotSubmitted
    const search = resourceSearch.toLowerCase()
    const result: typeof groupedNotSubmitted = {}
    Object.entries(groupedNotSubmitted).forEach(([key, group]) => {
      if (group.name.toLowerCase().includes(search) || group.email.toLowerCase().includes(search)) {
        result[key] = group
      }
    })
    return result
  }, [groupedNotSubmitted, resourceSearch])

  // Group by resource (submitter)
  const groupedByResource = useMemo(() => {
    const groups: { [key: string]: { name: string; email: string; items: PeriodHistoryItem[] } } = {}
    visibleHistory.forEach(item => {
      const key = item.submitted_by || 'unknown'
      const name = item.submitter_name || item.submitter_email || 'Unknown'
      const email = item.submitter_email || ''
      if (!groups[key]) groups[key] = { name, email, items: [] }
      groups[key].items.push(item)
    })
    return groups
  }, [visibleHistory])

  // Apply resource name search filter
  const filteredGroups = useMemo(() => {
    if (!resourceSearch.trim()) return groupedByResource
    const search = resourceSearch.toLowerCase()
    const result: typeof groupedByResource = {}
    Object.entries(groupedByResource).forEach(([key, group]) => {
      if (group.name.toLowerCase().includes(search) || group.email.toLowerCase().includes(search)) {
        result[key] = group
      }
    })
    return result
  }, [groupedByResource, resourceSearch])

  const handleReopen = async (periodId: string) => {
    setReopenLoading(periodId)
    setError('')
    setSuccess('')
    try {
      await timesheetService.reopenTimesheetPeriod(periodId)
      setSuccess('Timesheet re-enabled for editing. The resource can now modify and resubmit it.')
      setTimeout(() => setSuccess(''), 4000)
      setConfirmReopenId(null)
      await loadHistory()
    } catch (err: any) {
      setError(err?.message || 'Failed to re-enable timesheet')
    } finally {
      setReopenLoading(null)
    }
  }

  const handleToggleDetails = async (item: PeriodHistoryItem) => {
    if (expandedId === item.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(item.id)
    if (!detailRows[item.id] && item.submitted_by) {
      setDetailLoading(item.id)
      try {
        const startDate = item.period_start.split('T')[0]
        const endDate = item.period_end.split('T')[0]
        const entries = await timesheetService.getTimesheetEntries(item.submitted_by, startDate, endDate)
        const taskIds = [...new Set(entries.map(e => e.task_id).filter(Boolean) as string[])]
        const taskMap: { [id: string]: string } = {}
        await Promise.all(taskIds.map(async tid => {
          try {
            const task = await taskService.getTaskById(tid)
            if (task) taskMap[tid] = task.title
          } catch { /* ignore */ }
        }))
        const rows = buildDetailRows(entries, taskMap)
        setDetailRows(prev => ({ ...prev, [item.id]: rows }))
      } catch {
        setDetailRows(prev => ({ ...prev, [item.id]: [] }))
      } finally {
        setDetailLoading(null)
      }
    }
  }

  const buildDetailRows = (entries: TimesheetEntryUI[], taskMap: { [id: string]: string }): DetailRow[] => {
    const grouped: { [key: string]: TimesheetEntryUI[] } = {}
    entries.forEach(entry => {
      const key = `${entry.project_id}|${entry.task_id || 'none'}|${entry.is_billable ? 'b' : 'nb'}`
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(entry)
    })

    return Object.values(grouped).map(group => {
      const first = group[0]
      const row: DetailRow = {
        project_id: first.project_id,
        project_name: projects.find(p => p.id === first.project_id)?.title || 'Unknown',
        task_id: first.task_id,
        task_name: first.task_id ? (taskMap[first.task_id] || '') : '',
        is_billable: first.is_billable,
        monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0,
      }
      group.forEach(entry => {
        const [ey, em, ed] = entry.entry_date.split('T')[0].split('-').map(Number)
        const date = new Date(ey, em - 1, ed)
        const dayIdx = (date.getDay() + 6) % 7
        const dayKey = DAY_KEYS[dayIdx]
        row[dayKey] += entry.hours
        const commentKey = `${dayKey}_comment` as keyof DetailRow
        if (entry.description && !(row[commentKey] as string)) {
          (row as any)[commentKey] = entry.description
        }
      })
      return row
    })
  }

  const getRowTotal = (row: DetailRow): number =>
    row.monday + row.tuesday + row.wednesday + row.thursday + row.friday + row.saturday + row.sunday

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }

  const getWeekDateLabels = (periodStart: string): string[] => {
    const [y, m, d] = periodStart.split('T')[0].split('-').map(Number)
    const start = new Date(y, m - 1, d)
    return DAY_LABELS.map((_, i) => {
      const dt = new Date(start)
      dt.setDate(start.getDate() + i)
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    })
  }

  const clearFilters = () => {
    setFilterStatus('all')
    setFilterResource('all')
    setFilterStartDate('')
    setFilterEndDate('')
    setResourceSearch('')
  }

  const hasActiveFilters = filterStatus !== 'all' || filterResource !== 'all' || filterStartDate || filterEndDate || resourceSearch

  if (loading && history.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading timesheet history...</div>
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Timesheet History</h2>
          <p className={s.pageSubtitle}>
            {isResource && !isManager ? 'Your submitted timesheets' : 'All resource timesheets across the organization'}
          </p>
        </div>
        <button className={s.secondaryBtn} onClick={loadHistory}>Refresh</button>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, color: '#842029' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 4, color: '#0f5132' }}>
          {success}
        </div>
      )}

      {/* Filter bar */}
      <div className={s.card} style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Filter size={16} style={{ color: '#605e5c' }} />
          <strong style={{ fontSize: 13, color: '#323130' }}>Filters</strong>
          {hasActiveFilters && (
            <button onClick={clearFilters} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#0078d4', cursor: 'pointer', fontSize: 12, padding: 0 }}>
              Clear all
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#605e5c', display: 'block', marginBottom: 4 }}>Resource Name</label>
            <input
              type="text"
              placeholder="Search by name..."
              value={resourceSearch}
              onChange={e => setResourceSearch(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13 }}
            />
          </div>
          {!isResource || isManager ? (
            <div>
              <label style={{ fontSize: 12, color: '#605e5c', display: 'block', marginBottom: 4 }}>Resource</label>
              <select
                value={filterResource}
                onChange={e => setFilterResource(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13 }}
              >
                <option value="all">All resources</option>
                {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          ) : null}
          <div>
            <label style={{ fontSize: 12, color: '#605e5c', display: 'block', marginBottom: 4 }}>Status</label>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13 }}
            >
              <option value="all">All statuses</option>
            <option value="approved">Approved</option>
              <option value="submitted">Pending</option>
              <option value="rejected">Rejected</option>
              <option value="open">Open</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#605e5c', display: 'block', marginBottom: 4 }}>From Date</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={e => setFilterStartDate(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#605e5c', display: 'block', marginBottom: 4 }}>To Date</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={e => setFilterEndDate(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13 }}
            />
          </div>
        </div>
      </div>

{/* Not Submitted Section */}
      {!isResource || isManager ? (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Clock size={18} style={{ color: '#d83b01' }} />
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#323130', margin: 0 }}>
              Timesheets Not Submitted
            </h3>
            <span style={{ fontSize: 12, color: '#8a8888', marginLeft: 8 }}>
              {notSubmitted.length > 0 ? `${notSubmitted.length} pending` : ''}
            </span>
          </div>
          {Object.keys(filteredNotSubmitted).length === 0 ? (
            <div className={s.card} style={{ padding: 24, textAlign: 'center', color: '#6c757d' }}>
              <p style={{ fontSize: 13 }}>All resources have submitted their timesheets{filterStartDate || filterEndDate ? ' for the selected date range' : ''}.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Object.entries(filteredNotSubmitted).map(([resourceKey, group]) => (
                <div key={resourceKey} className={s.card} style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 16px', backgroundColor: '#fff4e6', borderBottom: '1px solid #fde0c2', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#323130' }}>{group.name}</span>
                      {group.email && <span style={{ fontSize: 12, color: '#8a8888', marginLeft: 8 }}>{group.email}</span>}
                    </div>
                    <span style={{ fontSize: 12, color: '#d83b01', fontWeight: 500 }}>
                      {group.items.length} week{group.items.length === 1 ? '' : 's'} not submitted
                    </span>
                  </div>
                  <div style={{ padding: '8px 16px' }}>
                    {group.items.map((item) => (
                      <div key={item.period_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f2f1' }}>
                        <span style={{ fontSize: 13, color: '#323130' }}>
                          {formatDate(item.period_start)} — {formatDate(item.period_end)}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {item.has_entries ? (
                            <span style={{ fontSize: 12, color: '#605e5c' }}>
                              {Number(item.draft_hours).toFixed(1)}h draft
                            </span>
                          ) : (
                            <span style={{ fontSize: 12, color: '#8a8888', fontStyle: 'italic' }}>
                              No hours logged
                            </span>
                          )}
                          <span style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 4,
                            backgroundColor: '#fff4e6',
                            color: '#d83b01',
                            fontWeight: 500,
                          }}>
                            Not submitted
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* History Section */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#323130', margin: 0 }}>
          Timesheet History
        </h3>
      </div>
      {Object.keys(filteredGroups).length === 0 ? (
        <div className={s.card} style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>
          <Clock size={40} style={{ color: '#c8c6c4', marginBottom: 12 }} />
          <p style={{ fontSize: 14 }}>No timesheet records found{hasActiveFilters ? ' for the selected filters' : ''}.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(filteredGroups).map(([resourceKey, group]) => {
            const totalApprovedHours = group.items
              .filter(i => i.status === 'approved')
              .reduce((sum, i) => sum + Number(i.total_hours || 0), 0)
            return (
              <div key={resourceKey} className={s.card} style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #edebe9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#323130' }}>{group.name}</span>
                    {group.email && <span style={{ fontSize: 12, color: '#8a8888', marginLeft: 8 }}>{group.email}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#605e5c' }}>
                    <span>{group.items.length} period{group.items.length === 1 ? '' : 's'}</span>
                    <span><strong>{totalApprovedHours.toFixed(1)}h</strong> approved</span>
                  </div>
                </div>
                <div>
                  {group.items.map(item => {
                    const statusInfo = STATUS_COLORS[item.status] || STATUS_COLORS.open
                    const isExpanded = expandedId === item.id
                    const rows = detailRows[item.id]
                    const dateLabels = getWeekDateLabels(item.period_start)
                    return (
                      <div key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', gap: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                            <span
                              style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, flexShrink: 0, backgroundColor: statusInfo.bg, color: statusInfo.text }}
                            >
                              {statusInfo.label}
                            </span>
                            <span style={{ fontSize: 13, color: '#323130', fontWeight: 500 }}>
                              {formatDate(item.period_start)} — {formatDate(item.period_end)}
                            </span>
                            <span style={{ fontSize: 12, color: '#605e5c' }}>
                              {Number(item.total_hours || 0).toFixed(1)}h
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            <button
                              className={s.secondaryBtn}
                              onClick={() => handleToggleDetails(item)}
                              style={{ padding: '4px 10px', fontSize: 12 }}
                            >
                              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              {isExpanded ? 'Hide' : 'Details'}
                            </button>
                            {canReopen && item.status === 'approved' && (
                              <button
                                className={s.secondaryBtn}
                                onClick={() => setConfirmReopenId(confirmReopenId === item.id ? null : item.id)}
                                disabled={reopenLoading === item.id}
                                style={{ padding: '4px 10px', fontSize: 12, color: '#d13438', borderColor: '#f5c2c7' }}
                              >
                                <RotateCcw size={12} /> Re-enable
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={{ fontSize: 11, color: '#8a8888', padding: '0 16px 8px 40px', display: 'flex', gap: 16 }}>
                          {item.submitted_at && <span>Submitted: {formatDateTime(item.submitted_at)}</span>}
                          {item.approved_at && <span>Approved: {formatDateTime(item.approved_at)}</span>}
                          {item.approver_name && <span>By: {item.approver_name}</span>}
                        </div>

                        {confirmReopenId === item.id && (
                          <div style={{ margin: '0 16px 10px', padding: 12, backgroundColor: '#fff4ce', border: '1px solid #ffe69c', borderRadius: 4 }}>
                            <div style={{ fontSize: 13, color: '#664d03', marginBottom: 8 }}>
                              <strong>Re-enable this timesheet for editing?</strong> The status will be set back to "open" and the resource will be able to modify and resubmit it.
                            </div>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                              <button className={s.secondaryBtn} onClick={() => setConfirmReopenId(null)} style={{ fontSize: 12 }}>Cancel</button>
                              <button
                                className={s.primaryBtn}
                                onClick={() => handleReopen(item.id)}
                                disabled={reopenLoading === item.id}
                                style={{ fontSize: 12, backgroundColor: '#d13438' }}
                              >
                                {reopenLoading === item.id ? 'Processing...' : 'Confirm Re-enable'}
                              </button>
                            </div>
                          </div>
                        )}

                        {isExpanded && (
                          <div style={{ padding: '0 16px 12px' }}>
                            {detailLoading === item.id ? (
                              <div style={{ padding: 16, textAlign: 'center', color: '#6c757d', fontSize: 13 }}>Loading daily breakdown...</div>
                            ) : rows && rows.length > 0 ? (
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr>
                                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: 140 }}>Project</th>
                                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: 120 }}>Task</th>
                                      <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 60 }}>Billable</th>
                                      {DAY_LABELS.map((day, i) => (
                                        <th key={day} style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 64 }}>
                                          <div>{day}</div>
                                          <div style={{ fontSize: 10, fontWeight: 400, color: '#8a8888' }}>{dateLabels[i]}</div>
                                        </th>
                                      ))}
                                      <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 60, fontWeight: 700 }}>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((row, ri) => (
                                      <React.Fragment key={ri}>
                                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{row.project_name}</td>
                                          <td style={{ padding: '6px 10px', color: '#605e5c' }}>{row.task_name || '—'}</td>
                                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                            <input type="checkbox" checked={row.is_billable} disabled style={{ opacity: 0.7 }} />
                                          </td>
                                          {DAY_KEYS.map(day => {
                                            const val = row[day] as number
                                            return (
                                              <td key={day} style={{ padding: '6px 6px', textAlign: 'center', color: val > 0 ? '#323130' : '#c8c6c4' }}>
                                                {val > 0 ? val.toFixed(2) : '—'}
                                              </td>
                                            )
                                          })}
                                          <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 700 }}>{getRowTotal(row).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                          <td colSpan={3} style={{ padding: '2px 10px 6px', fontSize: 11, color: '#8a8888' }}>Comments</td>
                                          {DAY_KEYS.map(day => {
                                            const commentKey = `${day}_comment` as keyof DetailRow
                                            const comment = row[commentKey] as string | undefined
                                            return (
                                              <td key={day} style={{ padding: '2px 6px 6px', fontSize: 11, color: '#8a8888', textAlign: 'center' }}>
                                                {comment || ''}
                                              </td>
                                            )
                                          })}
                                          <td></td>
                                        </tr>
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div style={{ padding: 16, textAlign: 'center', color: '#6c757d', fontSize: 13 }}>No detailed entries found for this period.</div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
